import React, { useState, useEffect } from 'react';
import { Card, Btn, Modal, Input, Select, Badge, Table, RNLLoader } from '../components/UI';
import * as api from '../api';

export default function AdminWorkflowsTab({ toast, selectedDef: externalSelectedDef, setSelectedDef: externalSetSelectedDef }) {
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState([]);
  const [roles, setRoles] = useState([]);

  // Modal states
  const [defModal, setDefModal] = useState(null); // { type: 'CREATE' | 'EDIT', data: {} }
  const [internalSelectedDef, setInternalSelectedDef] = useState(null); // The definition we are currently viewing
  const selectedDef = externalSelectedDef !== undefined ? externalSelectedDef : internalSelectedDef;
  const setSelectedDef = externalSetSelectedDef || setInternalSelectedDef;

  const [stepModal, setStepModal] = useState(null); // { type: 'CREATE' | 'EDIT', data: {} }
  const [roleModal, setRoleModal] = useState(null); // { stepId: '...', data: {} }
  const [confirmModal, setConfirmModal] = useState(null); // { title: '...', message: '...', onConfirm: async () => {} }
  const [deleting, setDeleting] = useState(false);

  // Load basic data
  const loadData = async () => {
    setLoading(true);
    try {
      const [defsRes, rolesRes] = await Promise.all([
        api.listWorkflowDefinitions(),
        api.listRoles()
      ]);
      setDefinitions(Array.isArray(defsRes) ? defsRes : (defsRes?.data || defsRes?.items || []));
      setRoles(Array.isArray(rolesRes) ? rolesRes : (rolesRes?.data || rolesRes?.items || []));
    } catch (e) {
      toast?.show?.('Failed to load workflow configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload the selected definition (to refresh steps/roles)
  const reloadSelectedDef = async (id) => {
    try {
      const res = await api.getWorkflowDefinition(id);
      setSelectedDef(res);
      // update it in the main list too
      setDefinitions(prev => prev.map(d => d.id === id ? res : d));
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveDef = async (e) => {
    e.preventDefault();
    const payload = {
      code: defModal.data.code,
      name: defModal.data.name,
      entity_type: defModal.data.entity_type,
      version: Number(defModal.data.version || 1),
      is_active: defModal.data.is_active !== false
    };

    try {
      if (defModal.type === 'CREATE') {
        await api.createWorkflowDefinition(payload);
        toast?.show?.('Workflow created', 'success');
      } else {
        await api.updateWorkflowDefinition(defModal.data.id, payload);
        toast?.show?.('Workflow updated', 'success');
        if (selectedDef?.id === defModal.data.id) {
          reloadSelectedDef(defModal.data.id);
        }
      }
      setDefModal(null);
      loadData();
    } catch (e) {
      toast?.show?.('Failed to save workflow', 'error');
    }
  };

  const handleDeleteDef = (id) => {
    setConfirmModal({
      title: 'Delete Workflow Definition',
      message: 'Are you sure you want to delete this workflow definition? This action cannot be undone and may break pending approvals.',
      onConfirm: async () => {
        try {
          await api.deleteWorkflowDefinition(id);
          toast?.show?.('Workflow deleted', 'success');
          setSelectedDef(null);
          loadData();
        } catch (e) {
          toast?.show?.('Delete failed', 'error');
        }
      }
    });
  };

  const handleSaveStep = async (e) => {
    e.preventDefault();
    const payload = {
      step_order: Number(stepModal.data.step_order),
      name: stepModal.data.name,
      approver_mode: stepModal.data.approver_mode || 'role_pick',
      reject_behavior: stepModal.data.reject_behavior || 'return_to_requester',
      require_comment_on_reject: stepModal.data.require_comment_on_reject !== false,
      is_final: stepModal.data.is_final === true,
      condition: stepModal.data.condition || {},
      sla_days: Number(stepModal.data.sla_days || 0)
    };

    try {
      if (stepModal.type === 'CREATE') {
        await api.createWorkflowStep(selectedDef.id, payload);
        toast?.show?.('Step created', 'success');
      } else {
        await api.updateWorkflowStep(stepModal.data.id, payload);
        toast?.show?.('Step updated', 'success');
      }
      setStepModal(null);
      reloadSelectedDef(selectedDef.id);
    } catch (e) {
      toast?.show?.('Failed to save step', 'error');
    }
  };

  const handleDeleteStep = (stepId) => {
    setConfirmModal({
      title: 'Delete Workflow Step',
      message: 'Are you sure you want to delete this workflow step? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.deleteWorkflowStep(stepId);
          toast?.show?.('Step deleted', 'success');
          reloadSelectedDef(selectedDef.id);
        } catch (e) {
          toast?.show?.('Failed to delete step', 'error');
        }
      }
    });
  };

  const handleAddRoleToStep = async (e) => {
    e.preventDefault();
    if (!roleModal.data.role_id) return;
    try {
      await api.addRoleToStep(roleModal.stepId, { role_id: roleModal.data.role_id });
      toast?.show?.('Role added', 'success');
      setRoleModal(null);
      reloadSelectedDef(selectedDef.id);
    } catch (e) {
      toast?.show?.('Failed to add role', 'error');
    }
  };

  const handleRemoveRoleFromStep = (stepId, roleId) => {
    setConfirmModal({
      title: 'Remove Role from Step',
      message: 'Are you sure you want to remove this role from the step?',
      onConfirm: async () => {
        try {
          await api.removeRoleFromStep(stepId, roleId);
          toast?.show?.('Role removed', 'success');
          reloadSelectedDef(selectedDef.id);
        } catch (e) {
          toast?.show?.('Failed to remove role', 'error');
        }
      }
    });
  };


  if (loading) return <RNLLoader />;

  // ----------------------------------------------------
  // VIEW: DETAILS (STEPS)
  // ----------------------------------------------------
  if (selectedDef) {
    const steps = [...(selectedDef.steps || [])].sort((a, b) => a.step_order - b.step_order);

    return (
      <div className="fade-in">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: '#0f172a', margin: 0 }}>
              {selectedDef.name} <span style={{ color: '#64748b', fontSize: 16, fontWeight: 400 }}>({selectedDef.code})</span>
            </h2>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              Entity: {selectedDef.entity_type} • Version: {selectedDef.version} • Status: {selectedDef.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Inactive</Badge>}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Btn onClick={() => setStepModal({ type: 'CREATE', data: { step_order: (steps.length + 1) * 10 } })}>Add Step</Btn>
          </div>
        </div>

        {steps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
            <p style={{ color: '#64748b', marginBottom: 16 }}>No steps configured for this workflow.</p>
            <Btn onClick={() => setStepModal({ type: 'CREATE', data: { step_order: 10 } })}>Add First Step</Btn>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {steps.map((step, idx) => (
              <Card key={step.id} style={{ padding: 16, border: '1px solid black', borderLeft: '4px solid #0b318bff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ background: '#e2e8f0', color: '#475569', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
                        {idx + 1}
                      </div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{step.name}</h3>
                      {step.is_final && <Badge variant="info">Final Step</Badge>}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', display: 'flex', gap: 16 }}>
                      <span>Order: {step.step_order}</span>
                      <span>Approver Mode: {step.approver_mode}</span>
                      <span>Reject Behavior: {step.reject_behavior}</span>
                      {step.sla_days > 0 && <span>SLA: {step.sla_days} days</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" style={{ background: '#eab308', color: '#fff', border: 'none', boxShadow: 'none' }} onClick={() => setStepModal({ type: 'EDIT', data: { ...step } })}>Edit</Btn>
                    <Btn size="sm" style={{ background: '#ef4444', color: '#fff', border: 'none', boxShadow: 'none' }} onClick={() => handleDeleteStep(step.id)}>Delete</Btn>
                  </div>
                </div>

                <div style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Authorized Roles</span>
                    <Btn size="sm" style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '4px 12px', fontSize: 12, boxShadow: 'none' }} onClick={() => setRoleModal({ stepId: step.id, data: { role_id: '' } })}>Add Role</Btn>
                  </div>

                  {(!step.roles || step.roles.length === 0) ? (
                    <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No roles assigned. This step cannot be actioned by anyone.</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {step.roles.map(r => {
                        const roleObj = roles.find(ro => (ro.id || ro.role_id) === r.role_id) || { display_name: r.role_id };
                        return (
                          <div key={r.role_id} style={{ display: 'flex', alignItems: 'center', background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                            {roleObj.display_name || roleObj.name || roleObj.role_name}
                            <button
                              onClick={() => handleRemoveRoleFromStep(step.id, r.role_id)}
                              style={{ background: 'none', border: 'none', color: '#4f46e5', marginLeft: 6, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                              title="Remove role"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* --- STEP MODAL --- */}
        {stepModal && (
          <Modal title={stepModal.type === 'CREATE' ? 'Add Workflow Step' : 'Edit Step'} onClose={() => setStepModal(null)} width={600}>
            <form onSubmit={handleSaveStep} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Input label="Step Name" required value={stepModal.data.name} onChange={v => setStepModal({ ...stepModal, data: { ...stepModal.data, name: v } })} placeholder="e.g. Line Manager Approval" />
                <Input label="Step Order (Number)" type="number" required value={stepModal.data.step_order} onChange={v => setStepModal({ ...stepModal, data: { ...stepModal.data, step_order: v } })} />

                <Select
                  label="Approver Mode"
                  options={[{ value: 'role_pick', label: 'Role / Step Pool (Shared Queue for Role)' }, { value: 'specific_user', label: 'Specific User (Assigned to named Person)' }]}
                  value={stepModal.data.approver_mode || 'role_pick'}
                  onChange={v => setStepModal({ ...stepModal, data: { ...stepModal.data, approver_mode: v } })}
                />

                <Select
                  label="Reject Behavior"
                  options={[{ value: 'return_to_requester', label: 'Return to Requester' }, { value: 'return_to_previous', label: 'Return to Previous Step' }, { value: 'cancel_workflow', label: 'Cancel Entire Workflow' }]}
                  value={stepModal.data.reject_behavior || 'return_to_requester'}
                  onChange={v => setStepModal({ ...stepModal, data: { ...stepModal.data, reject_behavior: v } })}
                />

                <Input label="SLA (Days)" type="number" value={stepModal.data.sla_days} onChange={v => setStepModal({ ...stepModal, data: { ...stepModal.data, sla_days: v } })} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={stepModal.data.is_final || false} onChange={e => setStepModal({ ...stepModal, data: { ...stepModal.data, is_final: e.target.checked } })} />
                  Is Final Step
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={stepModal.data.require_comment_on_reject !== false} onChange={e => setStepModal({ ...stepModal, data: { ...stepModal.data, require_comment_on_reject: e.target.checked } })} />
                  Require Comment on Reject
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
                <Btn variant="outline" type="button" onClick={() => setStepModal(null)}>Cancel</Btn>
                <Btn type="submit">Save Step</Btn>
              </div>
            </form>
          </Modal>
        )}

        {/* --- ROLE MODAL --- */}
        {roleModal && (
          <Modal title="Add Role to Step" onClose={() => setRoleModal(null)} width={400}>
            <form onSubmit={handleAddRoleToStep} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Select
                label="Select Role"
                required
                options={[{ value: '', label: '— Select Role —' }, ...roles.map(r => ({ value: r.id || r.role_id, label: r.display_name || r.name || r.role_name }))]}
                value={roleModal.data.role_id}
                onChange={v => setRoleModal({ ...roleModal, data: { ...roleModal.data, role_id: v } })}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
                <Btn variant="outline" type="button" onClick={() => setRoleModal(null)}>Cancel</Btn>
                <Btn type="submit" disabled={!roleModal.data.role_id}>Add Role</Btn>
              </div>
            </form>
          </Modal>
        )}

        {/* --- CONFIRMATION MODAL --- */}
        {confirmModal && (
          <Modal title={confirmModal.title} onClose={() => setConfirmModal(null)} width={450}>
            <div style={{ padding: 24 }}>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 24px 0', lineHeight: 1.5 }}>
                {confirmModal.message}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <Btn variant="outline" type="button" onClick={() => setConfirmModal(null)}>Cancel</Btn>
                <Btn 
                  type="button"
                  disabled={deleting}
                  loading={deleting}
                  onClick={async () => {
                    if (confirmModal?.onConfirm) {
                      setDeleting(true);
                      try {
                        await confirmModal.onConfirm();
                      } finally {
                        setDeleting(false);
                        setConfirmModal(null);
                      }
                    }
                  }} 
                  style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                >
                  {deleting ? 'Deleting...' : 'Confirm Delete'}
                </Btn>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ----------------------------------------------------
  // VIEW: LIST (DEFINITIONS - CARDS)
  // ----------------------------------------------------
  return (
    <div className="fade-in" style={{ padding: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: '#0f172a', margin: 0 }}>Workflow Configurations</h2>
        <Btn onClick={() => setDefModal({ type: 'CREATE', data: { is_active: true, version: 1 } })}>Add Workflow</Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {definitions.map(def => (
          <Card
            key={def.id}
            onClick={() => setSelectedDef(def)}
            style={{
              padding: 20,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              border: '1px solid black',
              position: 'relative'
            }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
          >
            <div style={{ position: 'absolute', top: 16, right: 16 }}>
              {def.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Inactive</Badge>}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ background: '#eff6ff', color: '#1d4ed8', width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{def.name}</h3>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{def.code}</div>
              </div>
            </div>

            <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Entity Type:</span>
                <span style={{ fontWeight: 500 }}>{def.entity_type}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Version:</span>
                <span style={{ fontWeight: 500 }}>{def.version}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Steps Configured:</span>
                <span style={{ fontWeight: 500 }}>{def.steps?.length || 0}</span>
              </div>
            </div>

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setDefModal({ type: 'EDIT', data: { ...def } }); }}>Edit</Btn>
              <Btn variant="outline" size="sm" style={{ color: '#ef4444', borderColor: '#fca5a5' }} onClick={(e) => { e.stopPropagation(); handleDeleteDef(def.id); }}>Delete</Btn>
            </div>
          </Card>
        ))}
        {definitions.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 48, background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
            <p style={{ color: '#64748b' }}>No workflow configurations found.</p>
          </div>
        )}
      </div>

      {/* --- DEFINITION MODAL --- */}
      {defModal && (
        <Modal title={defModal.type === 'CREATE' ? 'Add Workflow Configuration' : 'Edit Workflow Configuration'} onClose={() => setDefModal(null)} width={500}>
          <form onSubmit={handleSaveDef} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input label="Workflow Code" required value={defModal.data.code} onChange={v => setDefModal({ ...defModal, data: { ...defModal.data, code: v } })} placeholder="e.g. PR_APPROVAL" />
            <Input label="Workflow Name" required value={defModal.data.name} onChange={v => setDefModal({ ...defModal, data: { ...defModal.data, name: v } })} placeholder="e.g. Purchase Request Approval" />
            <Input label="Entity Type" required value={defModal.data.entity_type} onChange={v => setDefModal({ ...defModal, data: { ...defModal.data, entity_type: v } })} placeholder="e.g. purchase_request" />
            <Input label="Version" type="number" required value={defModal.data.version} onChange={v => setDefModal({ ...defModal, data: { ...defModal.data, version: v } })} />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginTop: 8 }}>
              <input type="checkbox" checked={defModal.data.is_active !== false} onChange={e => setDefModal({ ...defModal, data: { ...defModal.data, is_active: e.target.checked } })} />
              Active
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Btn variant="outline" type="button" onClick={() => setDefModal(null)}>Cancel</Btn>
              <Btn type="submit">Save</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* --- CONFIRMATION MODAL --- */}
      {confirmModal && (
        <Modal title={confirmModal.title} onClose={() => setConfirmModal(null)} width={450}>
          <div style={{ padding: 24 }}>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 24px 0', lineHeight: 1.5 }}>
              {confirmModal.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Btn variant="outline" type="button" onClick={() => setConfirmModal(null)}>Cancel</Btn>
              <Btn 
                type="button"
                disabled={deleting}
                loading={deleting}
                onClick={async () => {
                  if (confirmModal?.onConfirm) {
                    setDeleting(true);
                    try {
                      await confirmModal.onConfirm();
                    } finally {
                      setDeleting(false);
                      setConfirmModal(null);
                    }
                  }
                }} 
                style={{ background: '#ef4444', color: '#fff', border: 'none' }}
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
